import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrophy,
  faMedal,
  faArrowUpRightFromSquare,
  faPaperPlane,
  faPen,
  faTrashCan,
  faCircleCheck,
  faTriangleExclamation,
  faRotate,
  faHourglassHalf,
  faCalendarDays,
  faGift,
  faStar,
  faLink,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import Countdown from './Countdown';
import Participants from './Participants';
import './CompetitionsBoard.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const LEVEL = { all: 'All students', little: 'Little (6–8)', junior: 'Junior (9+)' };
const PLACE = { 1: '1st place', 2: '2nd place', 3: '3rd place' };
const day = (s, opts = { month: 'short', day: 'numeric' }) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', opts) : '';
};
const EMPTY = { title: '', link: '', note: '' };

/* Send / edit my project (students, while the challenge is open) */
const EntryForm = ({ comp, onSaved }) => {
  const [editing, setEditing] = useState(!comp.myEntry);
  const [form, setForm] = useState(comp.myEntry ? { title: comp.myEntry.title, link: comp.myEntry.link, note: comp.myEntry.note } : EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async (e) => {
    e.preventDefault();
    if (!/^https?:\/\/\S{3,}$/i.test(form.link.trim())) return setError('Paste the link to your project (it starts with https://).');
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API}/api/student/competitions/${comp.id}/entry`, form, auth());
      setEditing(false);
      onSaved('Your project was sent. Good luck! 🍀');
    } catch (err) {
      setError(err.response?.data?.error || 'Couldn’t send your project. Please try again.');
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const remove = async () => {
    if (!window.confirm('Remove your project from this challenge?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/api/student/competitions/${comp.id}/entry`, auth());
      setForm(EMPTY);
      setEditing(true);
      onSaved('Your project was removed.');
    } catch (err) {
      setError(err.response?.data?.error || 'Couldn’t remove it.');
    } finally {
      setBusy(false);
    }
  };

  if (comp.myEntry && !editing) {
    return (
      <div className="rmcmp-mine">
        <FontAwesomeIcon icon={faCircleCheck} />
        <div>
          <b>Your project is in!</b>
          <a href={comp.myEntry.link} target="_blank" rel="noopener noreferrer">
            {comp.myEntry.title || 'Open my project'} <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </a>
          <small>You can change it until {day(comp.endDate, { month: 'long', day: 'numeric' })}.</small>
        </div>
        <div className="rmcmp-mine-actions">
          <button type="button" onClick={() => setEditing(true)} disabled={busy} aria-label="Edit my project">
            <FontAwesomeIcon icon={faPen} />
          </button>
          <button type="button" className="is-danger" onClick={remove} disabled={busy} aria-label="Remove my project">
            <FontAwesomeIcon icon={faTrashCan} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="rmcmp-form" onSubmit={save}>
      <label>
        <span>Project link *</span>
        <input type="url" inputMode="url" placeholder="https://scratch.mit.edu/projects/…" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} disabled={busy} required />
      </label>
      <label>
        <span>Project name</span>
        <input type="text" maxLength={120} placeholder="My space game" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} disabled={busy} />
      </label>
      <div className="rmcmp-linknote rmcmp-form-wide">
        <FontAwesomeIcon icon={faLink} />
        <div>
          <b>Make sure everyone can open your link!</b>
          <span>
            Send the <b>project link</b> itself. Scratch: click <b>Share</b> on your project. Code.org, Thunkable, App Inventor, Replit, Tinkercad: use the <b>Share</b> or <b>public link</b>. AI Creators: use the tool’s <b>Share</b> link. Open the link in a private window to check it works.
          </span>
        </div>
      </div>
      <label className="rmcmp-form-wide">
        <span>What did you make?</span>
        <textarea rows={2} maxLength={500} placeholder="Tell the teachers about your project" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} disabled={busy} />
      </label>
      {error && <p className="rmcmp-error rmcmp-form-wide">{error}</p>}
      <div className="rmcmp-form-wide rmcmp-form-actions">
        {comp.myEntry && (
          <button type="button" className="rmcmp-btn rmcmp-btn--ghost" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </button>
        )}
        <button type="submit" className="rmcmp-btn" disabled={busy}>
          <FontAwesomeIcon icon={faPaperPlane} /> {busy ? 'Sending…' : comp.myEntry ? 'Save changes' : 'Send my project'}
        </button>
      </div>
    </form>
  );
};

/* Score buttons 1-10 (teachers, while voting is open) */
const Score = ({ entry, onScore }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (n) => {
    setBusy(true);
    await onScore(entry, n);
    setBusy(false);
  };
  return (
    <div className="rmcmp-score" role="radiogroup" aria-label={`Score for ${entry.title || entry.by}`}>
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <button key={n} type="button" role="radio" aria-checked={entry.myScore === n} className={entry.myScore === n ? 'is-on' : ''} onClick={() => pick(n)} disabled={busy}>
          {n}
        </button>
      ))}
    </div>
  );
};

const EntryCard = ({ e, mode, voting, onScore }) => (
  <li className={`rmcmp-entry ${e.rank ? `is-rank-${e.rank}` : ''} ${e.mine ? 'is-mine' : ''}`}>
    {e.rank && (
      <span className="rmcmp-medal">
        <FontAwesomeIcon icon={e.rank === 1 ? faTrophy : faMedal} /> {PLACE[e.rank]}
      </span>
    )}
    <div className="rmcmp-entry-who">
      {e.rank && e.photo && <img src={e.photo} alt="" loading="lazy" onError={(ev) => (ev.currentTarget.style.display = 'none')} />}
      <div>
        <h4>{e.title || 'Project'}</h4>
        <p className="rmcmp-by">
          by {e.by}
          {e.mine ? ' (you)' : ''}
        </p>
      </div>
    </div>
    {e.note && <p className="rmcmp-note">{e.note}</p>}
    <a className="rmcmp-open" href={e.link} target="_blank" rel="noopener noreferrer">
      Open project <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
    </a>
    {mode === 'teacher' && voting && <Score entry={e} onScore={onScore} />}
    {mode === 'teacher' && !voting && e.myScore && (
      <p className="rmcmp-myscore">
        <FontAwesomeIcon icon={faStar} /> Your score: {e.myScore}/10
      </p>
    )}
  </li>
);

/* "12 students are taking part · 5 projects sent" */
const PeopleHead = ({ c, mode }) => {
  const list = c.participants || [];
  const sent = list.filter((p) => p.sent).length;
  return (
    <h4 className="rmcmp-sub">
      <FontAwesomeIcon icon={faUsers} />{' '}
      {list.length
        ? `${list.length} ${list.length === 1 ? 'student is' : 'students are'} taking part${c.status === 'open' ? ` · ${sent} ${sent === 1 ? 'project' : 'projects'} sent` : ''}`
        : mode === 'teacher'
        ? 'No student joined yet'
        : 'Be the first to join!'}
    </h4>
  );
};

/* Before the start: "I accept the challenge" (my name shows with the others) */
const JoinBox = ({ comp, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const start = day(comp.startDate, { weekday: 'long', month: 'long', day: 'numeric' });

  const go = async (join) => {
    setBusy(true);
    setError('');
    try {
      if (join) await axios.post(`${API}/api/student/competitions/${comp.id}/join`, {}, auth());
      else await axios.delete(`${API}/api/student/competitions/${comp.id}/join`, auth());
      onSaved(join ? 'You’re in! 🚀 Start thinking about your project.' : 'You left the challenge.');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (comp.joined) {
    return (
      <div className="rmcmp-mine">
        <FontAwesomeIcon icon={faCircleCheck} />
        <div>
          <b>You accepted the challenge! 🚀</b>
          <small>You can send your project from {start}. Start thinking about your idea!</small>
        </div>
        <button type="button" className="rmcmp-leave" onClick={() => window.confirm('Leave this challenge?') && go(false)} disabled={busy}>
          Leave
        </button>
      </div>
    );
  }
  return (
    <div className="rmcmp-join">
      <p>
        Accept the challenge now: your name shows with the other participants, and you can send your project from <b>{start}</b>.
      </p>
      <button type="button" className="rmcmp-btn rmcmp-btn--big" onClick={() => go(true)} disabled={busy}>
        <FontAwesomeIcon icon={faTrophy} /> {busy ? 'Joining…' : 'I accept the challenge!'}
      </button>
      {error && <p className="rmcmp-error">{error}</p>}
    </div>
  );
};

const CompetitionCard = ({ c, mode, canEnter, onSaved, onScore, onTimeUp }) => {
  const voting = c.status === 'judging';
  const winners = c.entries.filter((e) => e.rank).sort((a, b) => a.rank - b.rank);
  const others = c.entries.filter((e) => !e.rank);
  return (
    <article className={`rmcmp-card is-${c.status}`}>
      <div className="rmcmp-card-head">
        <span className="rmcmp-level">{LEVEL[c.level] || LEVEL.all}</span>
        <span className="rmcmp-when">
          <FontAwesomeIcon icon={c.status === 'upcoming' ? faCalendarDays : c.status === 'open' ? faHourglassHalf : faCalendarDays} />
          {c.status === 'upcoming' && `Starts ${day(c.startDate)}`}
          {c.status === 'open' && `Send your project by ${day(c.endDate, { weekday: 'short', month: 'short', day: 'numeric' })}`}
          {c.status === 'judging' && 'Teachers are voting'}
          {c.status === 'finished' && `${day(c.startDate)} – ${day(c.endDate)}`}
        </span>
      </div>
      <h3>{c.title}</h3>
      {c.description && <p className="rmcmp-desc">{c.description}</p>}
      {c.prize && (
        <p className="rmcmp-prize">
          <FontAwesomeIcon icon={faGift} /> {c.prize}
        </p>
      )}

      {c.status === 'upcoming' && (
        <>
          <Countdown to={c.startsAt} label="Starts in" onDone={onTimeUp} />
          {mode === 'student' && canEnter && <JoinBox comp={c} onSaved={onSaved} />}
          <PeopleHead c={c} mode={mode} />
          <Participants list={c.participants || []} />
        </>
      )}

      {c.status === 'open' && (
        <>
          <Countdown to={c.endsAt} label="Time left to send your project" onDone={onTimeUp} />
          {mode === 'student' && canEnter && <EntryForm comp={c} onSaved={onSaved} />}
          <PeopleHead c={c} mode={mode} />
          <Participants list={c.participants || []} />
          {mode === 'teacher' && <p className="rmcmp-hint">Projects show here when the challenge closes. Then give each one a score from 1 to 10.</p>}
        </>
      )}

      {voting && mode === 'teacher' && (
        <p className="rmcmp-hint">
          {c.toScore > 0 ? `${c.toScore} ${c.toScore === 1 ? 'project' : 'projects'} still to score.` : 'You scored every project. Thank you!'} 1 = needs work, 10 = amazing.
        </p>
      )}
      {voting && mode === 'student' && (
        <p className="rmcmp-hint">The teachers are looking at every project. Winners are announced soon!</p>
      )}

      {winners.length > 0 && (
        <>
          <h4 className="rmcmp-sub">
            <FontAwesomeIcon icon={faTrophy} /> Winners
          </h4>
          <ul className="rmcmp-entries rmcmp-entries--winners">
            {winners.map((e) => (
              <EntryCard key={e.id} e={e} mode={mode} voting={voting} onScore={onScore} />
            ))}
          </ul>
        </>
      )}
      {others.length > 0 && (
        <>
          <h4 className="rmcmp-sub">{winners.length ? 'All projects' : `${others.length} ${others.length === 1 ? 'project' : 'projects'}`}</h4>
          <ul className="rmcmp-entries">
            {others.map((e) => (
              <EntryCard key={e.id} e={e} mode={mode} voting={voting} onScore={onScore} />
            ))}
          </ul>
        </>
      )}
      {(c.status === 'judging' || c.status === 'finished') && c.entries.length === 0 && <p className="rmcmp-hint">No projects were sent for this challenge.</p>}
    </article>
  );
};

/* The competitions page for students (mode="student") and teachers (mode="teacher") */
const CompetitionsBoard = ({ mode = 'student' }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const url = mode === 'teacher' ? `${API}/api/teacher/competitions` : `${API}/api/student/competitions`;
  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(url, auth());
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'We couldn’t load the competitions.');
    }
  }, [url]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const onSaved = (msg) => {
    setToast(msg);
    load();
  };

  // teachers: save the score, update it on the page right away
  const onScore = async (entry, score) => {
    try {
      await axios.put(`${API}/api/teacher/competitions/entry/${entry.id}/score`, { score }, auth());
      setData((d) => ({
        ...d,
        competitions: d.competitions.map((c) => {
          if (!c.entries.some((e) => e.id === entry.id)) return c;
          const entries = c.entries.map((e) => (e.id === entry.id ? { ...e, myScore: score } : e));
          return { ...c, entries, toScore: entries.filter((e) => !e.myScore).length };
        }),
      }));
    } catch (err) {
      setToast(err.response?.data?.error || 'Couldn’t save the score.');
    }
  };

  if (error && !data) {
    return (
      <div className="rmcmp">
        <div className="rmcmp-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <p>{error}</p>
          <button type="button" className="rmcmp-btn" onClick={load}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rmcmp" aria-busy="true">
        <div className="rmcmp-skel rmcmp-skel--head" />
        <div className="rmcmp-skel" />
      </div>
    );
  }

  const comps = data.competitions || [];
  const groups = [
    ['open', 'Open now'],
    ['judging', 'Teachers are voting'],
    ['upcoming', 'Coming soon'],
    ['finished', 'Past challenges'],
  ];

  return (
    <div className="rmcmp">
      <header className="rmcmp-head">
        <span className="rmcmp-eyebrow">
          <FontAwesomeIcon icon={faTrophy} /> Competitions
        </span>
        <h1>{mode === 'teacher' ? 'Student challenges' : 'Monthly challenges'}</h1>
        <p>
          {mode === 'teacher'
            ? 'A new challenge every month. Encourage your students to join, and when a challenge closes, score each project from 1 to 10.'
            : 'A new challenge every month. Build something, send the link, and the teachers pick the winners. Everyone can see the projects once the challenge closes.'}
        </p>
      </header>

      {mode === 'student' && data.wins?.length > 0 && (
        <>
        <h2 className="rmcmp-wins-title">
          <FontAwesomeIcon icon={faTrophy} /> My trophies
        </h2>
        <ul className="rmcmp-wins" aria-label="My trophies">
          {data.wins.map((w, i) => (
            <li key={i} className={`is-rank-${w.rank}`}>
              <FontAwesomeIcon icon={w.rank === 1 ? faTrophy : faMedal} />
              <span>
                <b>{PLACE[w.rank]}</b> {w.competition}
              </span>
            </li>
          ))}
        </ul>
        </>
      )}

      {comps.length === 0 ? (
        <div className="rmcmp-empty">
          <FontAwesomeIcon icon={faTrophy} />
          <p>The first challenge is coming soon. Stay tuned!</p>
        </div>
      ) : (
        groups.map(([key, label]) => {
          const list = comps.filter((c) => c.status === key);
          if (!list.length) return null;
          return (
            <section key={key} className="rmcmp-group">
              <h2>{label}</h2>
              {list.map((c) => (
                <CompetitionCard key={c.id} c={c} mode={mode} canEnter={data.canEnter} onSaved={onSaved} onScore={onScore} onTimeUp={load} />
              ))}
            </section>
          );
        })
      )}

      {toast && (
        <div className="rmcmp-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default CompetitionsBoard;
